import React, { useState, useEffect, useId, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiArrowRight } from 'react-icons/fi';
import { useLanguage } from '../utils/LanguageContext';
import { fetchInfomatics } from '../apiService';

// Custom hook for outside click detection
const useOutsideClick = (ref, callback) => {
  useEffect(() => {
    function handleClickOutside(event) {
      if (ref.current && !ref.current.contains(event.target)) {
        callback();
      }
    }
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [ref, callback]);
};

const CloseIcon = () => (
  <motion.svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="h-4 w-4 text-black"
  >
    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
    <path d="M18 6l-12 12" />
    <path d="M6 6l12 12" />
  </motion.svg>
);

const InfomaticsSection = () => {
  const [infomatics, setInfomatics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeInfomatic, setActiveInfomatic] = useState(null);
  const { language } = useLanguage();
  const id = useId();
  const ref = useRef(null);

  // Handle escape key press
  useEffect(() => {
    function onKeyDown(event) {
      if (event.key === "Escape") {
        setActiveInfomatic(null);
      }
    }

    if (activeInfomatic) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [activeInfomatic]);

  // Outside click handler
  useOutsideClick(ref, () => setActiveInfomatic(null));

  useEffect(() => {
    const loadLatestInfomatics = async () => {
      try {
        setLoading(true);
        const infomaticsData = await fetchInfomatics();
        setInfomatics(infomaticsData.slice(0, 4));
      } catch (err) {
        console.error('Error loading infomatics:', err);
        setError(language === 'MR' ? 'माहितीपत्रके लोड करण्यात अयशस्वी' : 'Failed to load infomatics');
      } finally {
        setLoading(false);
      }
    };

    loadLatestInfomatics();
  }, [language]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = { month: 'short', day: 'numeric', year: 'numeric' };
    return date.toLocaleDateString('en-US', options).toUpperCase();
  };

  const getImageSrc = (thumbnail) => {
    if (!thumbnail) return '/api/placeholder/600/400';
    if (thumbnail.startsWith('data:')) return thumbnail;
    if (thumbnail.startsWith('http')) return thumbnail;
    if (thumbnail.startsWith('uploads/')) {
      return `${process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5500'}/${thumbnail}`;
    }
    return '/api/placeholder/600/400';
  };

  // Transform infomatics for expandable card
  const transformedInfomatics = infomatics.map(infomatic => ({
    ...infomatic,
    content: () => (
      <p className="text-neutral-600">
        {language === 'MR' 
          ? `ही माहितीपत्रके ${formatDate(infomatic.date)} रोजी प्रकाशित झाली. अधिक माहितीसाठी खालील बटणावर क्लिक करा.`
          : `This infomatic was published on ${formatDate(infomatic.date)}. Click the button below to read more.`}
      </p>
    ),
    ctaText: language === 'MR' ? 'अधिक वाचा' : 'READ MORE',
    ctaLink: `/infomatic/${infomatic.slug || infomatic._id}`
  }));

  if (loading) {
    return (
      <section className="py-4 bg-white">
        <div className="max-w-4xl mx-auto px-4 md:px-4">
          <div className="flex items-center mb-10">
            <motion.div
              initial={{ opacity: 0, scaleX: 0 }}
              animate={{ opacity: 1, scaleX: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="flex-1 h-0.5 bg-gradient-to-r from-transparent to-red-800"
            />
            <div className="text-center">
              <motion.h2 
                className="text-2xl font-bold text-red-800 mr-6"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
              >
                {language === 'MR' ? 'ज्ञानाचे रंग' : 'COLORS OF KNOWLEDGE'}
              </motion.h2>
              <p className="text-sm text-gray-600 mt-2 italic">
                {language === 'MR' ? 'माहितीपत्रके आणि शैक्षणिक सामग्री' : 'Infographics and educational materials'}
              </p>
            </div>
            <motion.div
              initial={{ opacity: 0, scaleX: 0 }}
              animate={{ opacity: 1, scaleX: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="flex-1 h-0.5 bg-gradient-to-r from-red-800 to-transparent"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[...Array(4)].map((_, index) => (
              <div key={index} className="animate-pulse bg-gray-100 rounded-lg p-4 h-28 flex">
                <div className="bg-gray-300 rounded mr-4 w-16 h-16"></div>
                <div className="flex-1">
                  <div className="flex justify-between">
                    <div className="h-4 bg-gray-300 mb-3 w-3/4"></div>
                    <div className="h-3 bg-gray-300 w-1/4"></div>
                  </div>
                  <div className="h-8 bg-gray-300 rounded-full w-24 mt-1 ml-auto"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-4 bg-white">
        <div className="max-w-4xl mx-auto px-4 md:px-4">
          <div className="flex items-center mb-10">
            <motion.div
              initial={{ opacity: 0, scaleX: 0 }}
              animate={{ opacity: 1, scaleX: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="flex-1 h-0.5 bg-gradient-to-r from-transparent to-red-800"
            />
            <div className="text-center">
              <motion.h2 
                className="text-2xl font-bold text-red-800 mr-6"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
              >
                {language === 'MR' ? 'ज्ञानाचे रंग' : 'COLORS OF KNOWLEDGE'}
              </motion.h2>
              <p className="text-sm text-gray-600 mt-2 italic">
                {language === 'MR' ? 'माहितीपत्रके आणि शैक्षणिक सामग्री' : 'Infographics and educational materials'}
              </p>
            </div>
            <motion.div
              initial={{ opacity: 0, scaleX: 0 }}
              animate={{ opacity: 1, scaleX: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="flex-1 h-0.5 bg-gradient-to-r from-red-800 to-transparent"
            />
          </div>
          <div className="bg-red-50 border border-red-100 rounded-xl p-6 max-w-md mx-auto">
            <div className="text-4xl text-red-800 mb-3">⚠</div>
            <p className="text-black font-medium text-sm">{error}</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-4 bg-white">
      <div className="max-w-4xl mx-auto px-4 md:px-4">
        <div className="flex items-center mb-10">
          <motion.div
            initial={{ opacity: 0, scaleX: 0 }}
            animate={{ opacity: 1, scaleX: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="flex-1 h-0.5 bg-gradient-to-r from-transparent to-red-800"
          />
          <div className="text-center">
            <motion.h2 
              className="text-2xl font-bold text-red-800 mr-6"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              {language === 'MR' ? 'ज्ञानाचे रंग' : 'COLORS OF KNOWLEDGE'}
            </motion.h2>
            <p className="text-sm text-gray-600 mt-2 italic">
              {language === 'MR' ? 'माहितीपत्रके आणि शैक्षणिक सामग्री' : 'Infographics and educational materials'}
            </p>
          </div>
          <motion.div
            initial={{ opacity: 0, scaleX: 0 }}
            animate={{ opacity: 1, scaleX: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="flex-1 h-0.5 bg-gradient-to-r from-red-800 to-transparent"
          />
        </div>

        <AnimatePresence>
          {activeInfomatic && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/20 h-full w-full z-10"
            />
          )}
        </AnimatePresence>

        <AnimatePresence>
          {activeInfomatic ? (
            <div className="fixed inset-0 grid place-items-center z-[100]">
              <motion.button
                key={`button-${activeInfomatic.title}-${id}`}
                layout
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex absolute top-4 right-4 items-center justify-center bg-white rounded-full h-8 w-8 shadow-md"
                onClick={() => setActiveInfomatic(null)}
              >
                <CloseIcon />
              </motion.button>
              <motion.div
                layoutId={`card-${activeInfomatic.title}-${id}`}
                ref={ref}
                className="w-full max-w-md h-full md:h-fit md:max-h-[90%] flex flex-col bg-white rounded-xl overflow-hidden border border-gray-100 shadow-xl"
              >
                <motion.div layoutId={`image-${activeInfomatic.title}-${id}`} className="relative">
                  <img
                    width={200}
                    height={200}
                    src={getImageSrc(activeInfomatic.thumbnail)}
                    alt={activeInfomatic.title}
                    className="w-full h-64 object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                </motion.div>

                <div className="p-5">
                  <div className="flex justify-between items-start mb-4">
                    <div className="pr-4">
                      <motion.h3
                        layoutId={`title-${activeInfomatic.title}-${id}`}
                        className="font-bold text-xl text-gray-800"
                      >
                        {activeInfomatic.title}
                      </motion.h3>
                      <motion.p
                        layoutId={`description-${activeInfomatic.date}-${id}`}
                        className="text-gray-600 text-sm mt-1"
                      >
                        {formatDate(activeInfomatic.date)}
                      </motion.p>
                    </div>

                    <motion.a
                      layoutId={`button-${activeInfomatic.title}-${id}`}
                      href={activeInfomatic.ctaLink}
                      className="flex items-center px-4 py-2 text-sm rounded-full font-bold bg-red-600 text-white hover:bg-red-700 transition-colors whitespace-nowrap"
                    >
                      {activeInfomatic.ctaText}
                      <FiArrowRight className="ml-2" />
                    </motion.a>
                  </div>
                  <div className="pt-2 relative">
                    <motion.div
                      layout
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="text-gray-600 text-sm pb-6"
                    >
                      {activeInfomatic.content()}
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            </div>
          ) : null}
        </AnimatePresence>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {transformedInfomatics.map((infomatic, index) => (
            <motion.div
              layoutId={`card-${infomatic.title}-${id}`}
              key={`card-${infomatic.title}-${id}`}
              onClick={() => setActiveInfomatic(infomatic)}
              className="group cursor-pointer bg-white rounded-xl border border-gray-200 p-4 hover:shadow-sm transition-all duration-300 flex"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
            >
              {/* Thumbnail preview */}
              <motion.div 
                layoutId={`image-${infomatic.title}-${id}`}
                className="relative mr-4 flex-shrink-0"
              >
                <div className="bg-gray-200 border-2 border-dashed rounded-xl w-16 h-16 overflow-hidden">
                  <img
                    src={getImageSrc(infomatic.thumbnail)}
                    alt={infomatic.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              </motion.div>
              
              <div className="flex-1 min-w-0">
                <div className="flex justify-between items-start">
                  <motion.h3
                    layoutId={`title-${infomatic.title}-${id}`}
                    className="font-medium text-gray-800 line-clamp-2 pr-2 text-sm"
                  >
                    {infomatic.title}
                  </motion.h3>
                  <motion.p
                    layoutId={`description-${infomatic.date}-${id}`}
                    className="text-gray-500 text-xs whitespace-nowrap min-w-max pl-2"
                  >
                    {formatDate(infomatic.date)}
                  </motion.p>
                </div>
                
                <motion.div className="flex justify-end mt-2">
                  <motion.a
                    layoutId={`button-${infomatic.title}-${id}`}
                    href={infomatic.ctaLink}
                    onClick={(e) => e.stopPropagation()}
                    className="flex items-center text-xs text-red-600 hover:text-red-800 font-medium transition-colors group-hover:underline"
                  >
                    {infomatic.ctaText}
                    <FiArrowRight className="ml-1 opacity-0 group-hover:opacity-100 transition-opacity text-xs" />
                  </motion.a>
                </motion.div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default InfomaticsSection;